"use client";

import { useEffect, useRef, useState } from "react";
import { Feld, FeldHuelle, grundGroesse, type FeldEigenschaften, type FeldGroesseGrund } from "./feld";
import { useSchwebendeListe } from "./schwebende-liste";

/**
 * Trefferfeld (M-002 F6): Combobox nur für serverseitige Treffer. Das Feld ruft `suche` (in der Regel eine
 * Server-Action des Aufrufers) mit Verzögerung auf und zeigt die Treffer in einer Liste; gewählt wird genau ein
 * Treffer, der als gesperrtes Feld mit „Entfernen“ erscheint. Der Wert geht über ein verstecktes Feld (`name`) ins
 * Formular. Markup und Klassen der Preline-Combobox (`combobox-json-example`); das Verhalten läuft in React, weil das
 * Preline-Plugin nur über eine HTTP-Adresse lädt, nicht über eine Server-Action.
 *
 * Rechte: `suche` filtert mit derselben Prüffunktion wie die Action, die den Treffer später verwendet (r109, r110);
 * der Baustein zeigt nur, was zurückkommt (still gefiltert, kein „n ausgeblendet“). Scheitert die Suche, steht der
 * Fehlschlag-Satz („… Bitte erneut versuchen.“) als Hinweissatz am Feld (r105), nie der rohe Fehler.
 */
export interface Treffer {
  id: string;
  label: string;
  /** Zweite Zeile (Kennung, Rolle). */
  beschreibung?: string;
  /** Rechts in der Zeile (Status). */
  hinweis?: string;
  /** Sichtbar, aber nicht wählbar (ausgegraut, z. B. „bereits verknüpft“); Klick und Enter tun nichts (M-011 Teil 3). */
  gesperrt?: boolean;
}

type TrefferRest = {
  name?: string;
  suche: (anfrage: string) => Promise<Treffer[]>;
  platzhalter?: string;
  startText?: string;
  keineTreffer?: string;
  fehlschlagText?: string;
  /** Mindestlänge der Anfrage, bevor gesucht wird (Standard 0: schon beim Öffnen). */
  mindestlaenge?: number;
  verzoegerungMs?: number;
  entfernenLabel?: string;
  /** Vorgewählter Treffer (unkontrolliert). */
  vorgabe?: Treffer | null;
  /** Gewählter Treffer (kontrolliert, zusammen mit `beiWahl`). */
  gewaehlt?: Treffer | null;
  beiWahl?: (treffer: Treffer | null) => void;
  deaktiviert?: boolean;
};

const EINGABE: Record<FeldGroesseGrund, { normal: string; fehler: string }> = {
  standard: {
    normal:
      "py-2.5 sm:py-3 ps-4 pe-9 block w-full text-ellipsis bg-layer border-layer-line rounded-lg sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
    fehler:
      "py-2.5 sm:py-3 ps-4 pe-16 block w-full text-ellipsis bg-layer border-red-500 rounded-lg sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-red-500 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none",
  },
  kompakt: {
    normal:
      "py-1.5 sm:py-2 ps-3 pe-9 block w-full text-ellipsis bg-layer border-layer-line rounded-lg sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
    fehler:
      "py-1.5 sm:py-2 ps-3 pe-16 block w-full text-ellipsis bg-layer border-red-500 rounded-lg sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-red-500 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none",
  },
};
const LISTE =
  "absolute z-50 mt-1 w-full max-h-72 p-1 bg-select border border-select-line rounded-lg shadow-xl overflow-hidden overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";
const ZEILE =
  "cursor-pointer py-2 px-3 w-full flex items-center justify-between gap-x-2 text-sm text-select-item-foreground hover:bg-select-item-hover rounded-lg focus:outline-hidden focus:bg-select-item-focus";
const ZEILE_AKTIV =
  "cursor-pointer py-2 px-3 w-full flex items-center justify-between gap-x-2 text-sm text-select-item-foreground bg-select-item-hover rounded-lg focus:outline-hidden focus:bg-select-item-focus";
const ZEILE_GESPERRT = "cursor-default py-2 px-3 w-full flex items-center justify-between gap-x-2 text-sm text-muted-foreground-1 rounded-lg";
const MELDUNG = "py-2 px-3 text-sm text-muted-foreground-1";
const ENTFERNEN =
  "absolute inset-y-0 inset-e-0 flex items-center z-20 px-3 cursor-pointer text-muted-foreground rounded-e-md focus:outline-hidden focus:text-primary-focus";

export function Trefferfeld({
  name,
  suche,
  platzhalter = "Suchen …",
  startText = "Tippe zum Suchen.",
  keineTreffer = "Keine Treffer.",
  fehlschlagText = "Die Suche ist gerade nicht möglich. Bitte erneut versuchen.",
  mindestlaenge = 0,
  verzoegerungMs = 220,
  entfernenLabel = "Auswahl entfernen",
  vorgabe = null,
  gewaehlt: gewaehltKontrolliert,
  beiWahl,
  deaktiviert,
  ...feld
}: FeldEigenschaften & TrefferRest) {
  const kontrolliert = gewaehltKontrolliert !== undefined;
  const [interneWahl, setInterneWahl] = useState<Treffer | null>(vorgabe);
  const gewaehlt = kontrolliert ? gewaehltKontrolliert : interneWahl;
  const [eingabe, setEingabe] = useState("");
  const [offen, setOffen] = useState(false);
  const [treffer, setTreffer] = useState<Treffer[]>([]);
  const [laedt, setLaedt] = useState(false);
  const [fehlgeschlagen, setFehlgeschlagen] = useState(false);
  const [aktiv, setAktiv] = useState(-1);
  const anfrageNr = useRef(0);
  const sucheRef = useRef(suche);
  sucheRef.current = suche;
  // Die offene Liste liegt fest am Fenster, damit sie im scrollenden Modal-Körper nicht abgeschnitten wird (schwebende-liste.ts).
  const ankerRef = useRef<HTMLDivElement>(null);
  const listeRef = useRef<HTMLDivElement>(null);
  useSchwebendeListe(offen && !gewaehlt, listeRef, ankerRef, `${treffer.length}|${laedt}|${fehlgeschlagen}`);

  useEffect(() => {
    if (!offen || gewaehlt) return;
    if (eingabe.trim().length < mindestlaenge) {
      anfrageNr.current += 1;
      setTreffer([]);
      setLaedt(false);
      return;
    }
    const nr = ++anfrageNr.current;
    setLaedt(true);
    const h = setTimeout(() => {
      sucheRef
        .current(eingabe.trim())
        .then((r) => {
          if (nr !== anfrageNr.current) return;
          setTreffer(r);
          setFehlgeschlagen(false);
          setAktiv(-1);
        })
        .catch((fehler: unknown) => {
          if (nr !== anfrageNr.current) return;
          console.error("Trefferfeld: Suche fehlgeschlagen", fehler);
          setTreffer([]);
          setFehlgeschlagen(true);
        })
        .finally(() => {
          if (nr === anfrageNr.current) setLaedt(false);
        });
    }, verzoegerungMs);
    return () => clearTimeout(h);
  }, [offen, eingabe, gewaehlt, mindestlaenge, verzoegerungMs]);

  const waehle = (t: Treffer | null) => {
    if (t?.gesperrt) return;
    if (!kontrolliert) setInterneWahl(t);
    setOffen(false);
    setEingabe("");
    setTreffer([]);
    beiWahl?.(t);
  };

  return (
    <Feld {...feld} hinweissatz={feld.hinweissatz ?? (fehlgeschlagen && !gewaehlt ? fehlschlagText : undefined)}>
      {(a) => {
        const listeId = `${a.id}-liste`;
        const klassen = a.fehlerhaft ? EINGABE[grundGroesse(a.groesse)].fehler : EINGABE[grundGroesse(a.groesse)].normal;
        return (
          <>
            {name && <input type="hidden" name={name} value={gewaehlt?.id ?? ""} />}
            {gewaehlt ? (
              <FeldHuelle>
                {/* Preline: component/combobox/combobox-json-example */}
                <input
                  id={a.id}
                  type="text"
                  readOnly
                  value={gewaehlt.label}
                  className={klassen}
                  disabled={deaktiviert}
                  aria-describedby={a["aria-describedby"]}
                  aria-invalid={a["aria-invalid"]}
                />
                {!deaktiviert && (
                  <button type="button" className={ENTFERNEN} aria-label={entfernenLabel} onClick={() => waehle(null)}>
                    <svg
                      className="shrink-0 size-3.5"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                )}
              </FeldHuelle>
            ) : (
              <div
                ref={ankerRef}
                className="relative"
                // Esc-Vorrang (W130 a): solange die Liste offen ist, schließt Esc zuerst nur sie, nicht das Modal (modal-verhalten.ts).
                data-esc-eigen={offen ? "offen" : undefined}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOffen(false);
                }}
              >
                {/* Preline: component/combobox/combobox-json-example */}
                <input
                  id={a.id}
                  type="text"
                  role="combobox"
                  aria-expanded={offen}
                  aria-controls={listeId}
                  aria-autocomplete="list"
                  aria-activedescendant={aktiv >= 0 ? `${listeId}-${aktiv}` : undefined}
                  aria-describedby={a["aria-describedby"]}
                  aria-invalid={a["aria-invalid"]}
                  autoComplete="off"
                  value={eingabe}
                  placeholder={platzhalter}
                  disabled={deaktiviert}
                  className={klassen}
                  onChange={(e) => {
                    setEingabe(e.target.value);
                    setOffen(true);
                  }}
                  onFocus={() => setOffen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setOffen(true);
                      setAktiv((i) => Math.min(treffer.length - 1, i + 1));
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setAktiv((i) => Math.max(0, i - 1));
                    } else if (e.key === "Enter" && offen && aktiv >= 0 && treffer[aktiv]) {
                      e.preventDefault();
                      waehle(treffer[aktiv]!);
                    } else if (e.key === "Escape") {
                      setOffen(false);
                    }
                  }}
                />
                {offen && (
                  <div ref={listeRef} className={LISTE}>
                    <ul id={listeId} role="listbox" aria-label={feld.label}>
                      {treffer.map((t, i) => (
                        <li
                          key={t.id}
                          id={`${listeId}-${i}`}
                          role="option"
                          aria-selected={i === aktiv}
                          aria-disabled={t.gesperrt || undefined}
                          className={t.gesperrt ? ZEILE_GESPERRT : i === aktiv ? ZEILE_AKTIV : ZEILE}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => waehle(t)}
                        >
                          <span className="grow min-w-0 truncate">
                            <span className={t.gesperrt ? "font-medium" : "font-medium text-foreground"}>{t.label}</span>
                            {t.beschreibung && <span className="ms-1 text-muted-foreground-1">{t.beschreibung}</span>}
                          </span>
                          {t.hinweis && <span className="shrink-0 text-xs text-muted-foreground-1">{t.hinweis}</span>}
                        </li>
                      ))}
                    </ul>
                    {treffer.length === 0 && !fehlgeschlagen && (
                      <p className={MELDUNG} role="status">
                        {laedt ? "Suche läuft …" : eingabe.trim() === "" ? startText : keineTreffer}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        );
      }}
    </Feld>
  );
}
